import React, { FC, useEffect, useState, lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { showBlog } from "../../core/blog/requests";
import { SocialMeta } from "../../components/SocialMeta";
import { useLanguage } from "../../context/LanguageContext";
import { Blog } from "../../models/blog/Blog";
import { toAbsoluteUrl } from "../../helpers";
import moment from "moment/moment";
import { t } from "i18next";
import { preloadImages } from "../../helpers/general";

const BlogShowCard = lazy(() => import("./components/blogShowCard"));
const Hero = lazy(() => import("../../components/Hero"));
const CardLayoutThree = lazy(
  () => import("../../components/cards/CardLayoutThree")
);
const AsideImage = lazy(() => import("../../layout/AsideImage"));

const BlogShow: FC = () => {
  const { currentLanguageCode } = useLanguage();
  const [blog, setBlog] = useState<Blog | null>(null);
  const params = useParams();
  const [breadcrumb, setBreadcrumb] = useState([
    { label: t("home.home"), href: "/" },
    { label: "Blog", href: "/blogs" },
    { label: "", href: "" },
  ]);
  //const path = `${process.env.REACT_APP_IP}/images/blogs`;
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await showBlog(params.slug, currentLanguageCode);
        const blogData = response.data.data;
        setBlog(blogData);
        setBreadcrumb([
          { label: t("home.home"), href: "/" },
          { label: "Blog", href: "/blogs" },
          { label: blogData.title, href: "" },
        ]);

        // Preload related blog images if they exist
        if (blogData.related_blogs) {
          const imageUrls = blogData.related_blogs.map((relatedBlog: any) =>
            toAbsoluteUrl(relatedBlog.image)
          );

          //  toAbsoluteUrl(relatedBlog.image));
          await preloadImages(imageUrls);
        }
      } catch (error) {
        console.error("Failed to fetch blog data:", error);
      }
    };

    fetchBlog().then();
  }, [params.slug, currentLanguageCode]);

  return (
    <>
      <SocialMeta
        title={blog?.title}
        keyword="blog"
        description={blog?.meta_description}
        name="Garden Of Life"
        link={window.location.href}
      />
      <section>
        <div className="container">
          <Suspense fallback={<></>}>
            <Hero breadcrumb={breadcrumb} />
          </Suspense>
          <Suspense fallback={<></>}>
            {blog && <BlogShowCard blog={blog} />}
          </Suspense>
          <Suspense fallback={<></>}>
            {blog &&
              "related_blogs" in blog &&
              blog.related_blogs &&
              blog?.related_blogs?.length > 0 && (
                <div className="row justify-content-center pt-5 pb-5">
                  <div className="col-md-12 text-center">
                    <h2 className="title-primary-2 lg">Related Posts</h2>
                  </div>
                  <div className="row mt-5 pt-5 mb-3">
                    {blog.related_blogs
                      ?.filter((relatedBlog: any) => relatedBlog.id !== blog.id)
                      .map((relatedBlog: any) => (
                        <CardLayoutThree
                          key={relatedBlog.id}
                          btnText="Read More"
                          imageClass="blog-image"
                          image={toAbsoluteUrl(relatedBlog.image)}
                          title={relatedBlog.title}
                          slug={`blog/${relatedBlog.slug}`}
                          description={moment(relatedBlog.created_at)
                            .locale("en")
                            .format("DD MMMM[, ] YYYY")}
                        />
                      ))}
                  </div>
                </div>
              )}
          </Suspense>
          <Suspense fallback={<></>}>
            <AsideImage className="placeholder-3" />
          </Suspense>
        </div>
      </section>
    </>
  );
};

export default BlogShow;
